import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

/** Matches PlanRecord + teamCount from backend PlansAdminService. */
export interface AdminPlan {
  code: string;
  name: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  maxMembers: number | null;
  maxProjects: number | null;
  storageQuotaBytes: number;
  storageQuotaPerSeatBytes: number | null;
  maxFileSizeBytes: number;
  features: Record<string, boolean>;
  trialDays: number;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
  teamCount: number;
}

export function adminPlansKey(): QueryKey {
  return ['admin', 'plans'];
}
export function adminPlanKey(code: string): QueryKey {
  return ['admin', 'plan', code];
}
export function adminPlanHistoryKey(code: string): QueryKey {
  return ['admin', 'plan', code, 'history'];
}

export function useAdminPlans() {
  return useQuery({
    queryKey: adminPlansKey(),
    queryFn: () => api<AdminPlan[]>('/api/admin/plans'),
    staleTime: 60_000,
  });
}

export function useAdminPlan(code: string | undefined) {
  return useQuery({
    queryKey: code ? adminPlanKey(code) : ['admin', 'plan', 'noop'],
    queryFn: () => {
      if (!code) throw new Error('plan code required');
      return api<AdminPlan>(`/api/admin/plans/${code}`);
    },
    enabled: Boolean(code),
  });
}

export interface PlanPatch {
  name?: string;
  monthlyPriceCents?: number;
  annualPriceCents?: number;
  maxMembers?: number | null;
  maxProjects?: number | null;
  storageQuotaBytes?: number;
  storageQuotaPerSeatBytes?: number | null;
  maxFileSizeBytes?: number;
  features?: Record<string, boolean>;
  trialDays?: number;
  isActive?: boolean;
  isPublic?: boolean;
  sortOrder?: number;
}

export interface PreviewResult {
  affectedTeams: number;
  teamsExceedingNewLimits: Array<{ teamId: string; teamSlug: string; reasons: string[] }>;
  warnings: string[];
  cooldown: { isInCooldown: boolean; cooldownEndsAt: string | null };
}

export function usePlanPreview() {
  return useMutation({
    mutationFn: ({ code, patch }: { code: string; patch: PlanPatch }) =>
      api<PreviewResult>(`/api/admin/plans/${code}/preview`, {
        method: 'POST',
        body: { patch },
      }),
  });
}

export function usePlanApply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      code,
      patch,
      reason,
      confirmText,
    }: {
      code: string;
      patch: PlanPatch;
      reason: string;
      confirmText: string;
    }) =>
      api<AdminPlan>(`/api/admin/plans/${code}`, {
        method: 'PATCH',
        body: { patch, reason, confirmText },
      }),
    onSuccess: (plan) => {
      qc.setQueryData(adminPlanKey(plan.code), { ...plan, teamCount: undefined });
      void qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'plan', plan.code, 'history'] });
    },
  });
}

export interface PlanChangeHistory {
  id: string;
  changedBy: string;
  reason: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  affectedTeamsCount: number | null;
  isRevert: boolean;
  revertsChangeId: string | null;
  changedAt: string;
}

export function usePlanHistory(code: string | undefined) {
  return useQuery({
    queryKey: code ? adminPlanHistoryKey(code) : ['admin', 'plan', 'noop', 'history'],
    queryFn: () => {
      if (!code) throw new Error('plan code required');
      return api<PlanChangeHistory[]>(`/api/admin/plans/${code}/history`);
    },
    enabled: Boolean(code),
  });
}

export function usePlanRevert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, changeId, reason }: { code: string; changeId: string; reason: string }) =>
      api<AdminPlan>(`/api/admin/plans/${code}/revert/${changeId}`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: (plan) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'plan', plan.code] });
      void qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}
